import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ApiClient, CreatedKey, NewKeyInput } from "@/pages/admin/apikeys/hooks/useApiKeys";

interface ApiKeyModalProps {
  open: boolean;
  client: ApiClient | null;
  onClose: () => void;
  onSubmit: (input: NewKeyInput) => Promise<CreatedKey>;
  notify: (text: string, tone?: "success" | "error") => void;
}

// 签发 API Key：填写限流 / 配额 / 到期后签发，明文密钥仅在签发成功时展示一次。
export default function ApiKeyModal({ open, client, onClose, onSubmit, notify }: ApiKeyModalProps) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [rate, setRate] = useState("60");
  const [quota, setQuota] = useState("0");
  const [expiresAt, setExpiresAt] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName("");
    setRate("60");
    setQuota("0");
    setExpiresAt("");
    setError(null);
    setIssued(null);
    setCopied(false);
  }, [open]);

  if (!open || !client) return null;

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      const result = await onSubmit({
        clientId: client.id,
        name: name.trim() || t("apikeys.keyNameDefault"),
        rateLimitPerMin: Number(rate) || 60,
        quotaPerDay: Number(quota) || 0,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
      });
      setIssued(result.key);
      notify(t("apikeys.keyCreated"), "success");
    } catch (err) {
      const message = err instanceof Error ? err.message : t("apikeys.actionFailed");
      setError(message);
      notify(message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    if (!issued) return;
    try {
      await navigator.clipboard.writeText(issued);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setError(t("apikeys.actionFailed"));
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-foreground-950/40 px-4">
      <div className="w-full max-w-lg rounded-lg border border-background-200 bg-background-50 p-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="font-heading text-base font-semibold text-foreground-950">
              {issued ? t("apikeys.keyIssuedTitle") : t("apikeys.newKeyTitle")}
            </h3>
            <p className="mt-0.5 text-xs text-foreground-500">
              {issued ? t("apikeys.keyIssuedDesc") : `${client.name} · ${t("apikeys.newKeySubtitle")}`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100"
            aria-label={t("apikeys.cancel")}
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {issued ? (
          <div className="mt-4">
            <div className="flex items-start gap-2.5 rounded-md border border-accent-200 bg-accent-50 px-3.5 py-3">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center text-accent-700">
                <i className="ri-alert-line" />
              </span>
              <p className="text-xs leading-relaxed text-accent-800">{t("apikeys.keyIssuedDesc")}</p>
            </div>
            <div className="mt-3 flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-3">
              <code className="min-w-0 flex-1 break-all font-mono text-sm text-foreground-900">{issued}</code>
              <button
                type="button"
                onClick={() => void handleCopy()}
                className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 py-1.5 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className={copied ? "ri-checkbox-circle-line" : "ri-file-copy-line"} />
                </span>
                {copied ? t("apikeys.copied") : t("apikeys.copyKey")}
              </button>
            </div>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                {t("apikeys.done")}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="mt-4 flex flex-col gap-4">
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-foreground-700">{t("apikeys.keyName")}</span>
                <input
                  type="text"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder={t("apikeys.keyNameDefault")}
                  className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                />
              </label>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="flex flex-col gap-1.5">
                  <span className="text-xs font-medium text-foreground-700">{t("apikeys.fieldRate")}</span>
                  <input
                    type="number"
                    min={1}
                    value={rate}
                    onChange={(event) => setRate(event.target.value)}
                    className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
                  />
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="text-xs font-medium text-foreground-700">{t("apikeys.fieldQuota")}</span>
                  <input
                    type="number"
                    min={0}
                    value={quota}
                    onChange={(event) => setQuota(event.target.value)}
                    className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
                  />
                </label>
              </div>
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-foreground-700">{t("apikeys.fieldExpires")}</span>
                <input
                  type="datetime-local"
                  value={expiresAt}
                  onChange={(event) => setExpiresAt(event.target.value)}
                  className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
                />
              </label>
            </div>

            {error && (
              <p className="mt-3 flex items-center gap-1.5 rounded-md border border-accent-200 bg-accent-50 px-3 py-2 text-xs text-accent-800">
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-error-warning-line" />
                </span>
                {error}
              </p>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
              >
                {t("apikeys.cancel")}
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => void handleSubmit()}
                className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? t("apikeys.saving") : t("apikeys.create")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}